import { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Configure from 'assets/unifiedCommunication/Configure.svg';
import Button from 'common-ui/button';
import MobileList from 'common-ui/mobileList';
import Switch from 'common-ui/switch';
import { Table } from 'common-ui/table';
import {
  getAllPermissionsByAccessType,
  togglePermissionStatus,
} from 'features/chat/chatActions';
import { selectAllUserPermissions } from 'features/chat/chatSlice';
import { getUsers } from 'features/users/usersActions';
import { selectUsers, selectUsersCount } from 'features/users/usersSlice';
import useDebounce from 'hooks/useDebounce';

import ChannelConfigurator from '../components/ChannelConfigurator';
import PlatformIcons from '../components/PlatformIcons';
import { useChatSearchParams } from '../useSearchData';
import {
  ConfigureButton,
  EmptyState,
  EmptyStateText,
  EmptyStateTitle,
  MobileExpandedWrapper,
  MobileItem,
  MobileLabel,
  MobileRow,
  MobileValue,
  SearchButton,
  StatusText,
  StatusWrapper,
  TableWrapper,
} from './ByUser.styles';
import { Filter } from './Filter';

const ByUser = () => {
  const dispatch = useDispatch();
  const { searchData, setChatSearchData } = useChatSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const users = useSelector(selectUsers);
  const allUserPermissions = useSelector(selectAllUserPermissions);
  const [active, setActive] = useState(false);
  const count = useSelector(selectUsersCount);
  const pagesCount = Math.ceil(count / searchData.limit);
  const [tableData, setTableData] = useState([]);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [channelPermissions, setChannelPermissions] = useState({});
  const [first, setFirst] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [name, setName] = useState(searchData.name || '');
  const [surname, setSurname] = useState(searchData.surname || '');
  const [email, setEmail] = useState(searchData.email || '');

  const hasActiveSearch = Boolean(searchData.name || searchData.surname || searchData.email);

  const getPermissionRecord = (userId) => {
    return allUserPermissions.find(
      (perm) => perm.targetId === userId || perm.userId === userId
    );
  };

  const getStatus = (userId) => {
    const permRecord = getPermissionRecord(userId);
    if (!permRecord) return 'Disabled';
    return permRecord.isActive ? 'Active' : 'Disabled';
  };

  const handleToggleStatus = (userId) => {
    const permRecord = getPermissionRecord(userId);
    if (!permRecord) return;

    dispatch(
      togglePermissionStatus({
        permissionId: permRecord._id,
        isActive: !permRecord.isActive,
        accessType: 'user',
      })
    );
  };

  const parsePermissions = (userId) => {
    const viewPlatforms = [];
    const replyPlatforms = [];

    const userPermission = getPermissionRecord(userId);

    if (userPermission?.permissions) {
      userPermission.permissions.forEach((perm) => {
        if (!perm.isDenied) {
          if (perm.type === 'view_only') {
            viewPlatforms.push(perm.platform);
          } else if (perm.type === 'view_and_answer') {
            replyPlatforms.push(perm.platform);
          }
        }
      });
    }

    return { viewPlatforms, replyPlatforms };
  };

  const debouncedName = useDebounce(name, 500);
  const debouncedSurname = useDebounce(surname, 500);
  const debouncedEmail = useDebounce(email, 500);

  const currentPage = useMemo(() => {
    return Math.floor((searchData.offset || 0) / (searchData.limit || 10)) + 1;
  }, [searchData.offset, searchData.limit]);

  const handleClearAll = () => {
    setName('');
    setFirst(false);
    setSurname('');
    setEmail('');
    setHasSearched(false);
    setTableData([]);
    setChatSearchData({ name: '', surname: '', email: '' });
  };

  const columns = [
    {
      key: 'fullName',
      title: 'Full Name',
      dataIndex: 'fullName',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
    },
    {
      key: 'occupation',
      title: 'Occupation',
      dataIndex: 'occupation',
    },
    {
      key: 'role',
      title: 'Role',
      dataIndex: 'role',
    },
    {
      key: 'viewAccess',
      title: 'View',
      dataIndex: 'id',
      render: (userId) => {
        const { viewPlatforms } = parsePermissions(userId);
        return <PlatformIcons platforms={viewPlatforms} />;
      },
    },
    {
      key: 'viewReply',
      title: 'View and Reply',
      dataIndex: 'id',
      render: (userId) => {
        const { replyPlatforms } = parsePermissions(userId);
        return <PlatformIcons platforms={replyPlatforms} />;
      },
    },
    {
      key: 'channels',
      title: 'Channels',
      render: (_, row) => (
        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedUser(row);
            setIsChannelModalOpen(true);
          }}
        >
          <img src={Configure} alt="configure" />
          Configure
        </ConfigureButton>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'id',
      render: (userId) => {
        const status = getStatus(userId);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(userId);

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(userId)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  const mobileColumns = [
    {
      key: 'fullName',
      title: 'User',
      dataIndex: 'fullName',
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'id',
      render: (userId) => {
        const status = getStatus(userId);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(userId);

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(userId)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  const renderExpandable = (row) => {
    const { viewPlatforms, replyPlatforms } = parsePermissions(row.id);

    return (
      <MobileExpandedWrapper>
        <MobileItem>
          <MobileLabel>Email:</MobileLabel>
          <MobileValue>{row.email}</MobileValue>
        </MobileItem>

        <MobileItem>
          <MobileLabel>Occupation:</MobileLabel>
          <MobileValue>{row.occupation}</MobileValue>
        </MobileItem>

        <MobileItem>
          <MobileLabel>Role:</MobileLabel>
          <MobileValue>{row.role}</MobileValue>
        </MobileItem>

        <MobileRow>
          <MobileLabel>View</MobileLabel>
          <PlatformIcons platforms={viewPlatforms} />
        </MobileRow>

        <MobileRow>
          <MobileLabel>View and Reply</MobileLabel>
          <PlatformIcons platforms={replyPlatforms} />
        </MobileRow>

        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedUser(row);
            setIsChannelModalOpen(true);
          }}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          <img src={Configure} alt="configure" />
          Configure
        </ConfigureButton>
      </MobileExpandedWrapper>
    );
  };

  const onPaginationChange = (page) => {
    setFirst(false);
    setChatSearchData({ offset: (page - 1) * searchData.limit });
  };

  const handleChange = (value, field) => {
    setFirst(false);
    if (field === 'name') {
      setName(value);
    } else if (field === 'surname') {
      setSurname(value);
    } else if (field === 'email') {
      setEmail(value);
    }
  };

  useEffect(() => {
    if (hasActiveSearch) {
      setHasSearched(true);
      dispatch(getUsers({ ...searchData }));
    }
  }, [dispatch, JSON.stringify(searchData), hasActiveSearch]);

  useEffect(() => {
    if (users && hasActiveSearch) {
      const formattedData = users.map((user) => ({
        id: user.uuid,
        uuid: user.uuid,
        fullName: user.name + ' ' + user.surname,
        email: user.email,
        occupation: user.occupation,
        role: user.userType,
        viewAccess: false,
        viewReply: false,
        status: 'Active',
      }));
      setTableData(formattedData);
    } else if (!hasActiveSearch) {
      setTableData([]);
    }
  }, [users, hasActiveSearch]);

  useEffect(() => {
    if (!first) {
      setChatSearchData({ name: debouncedName });
    }
  }, [debouncedName]);

  useEffect(() => {
    if (!first) {
      setChatSearchData({ surname: debouncedSurname });
    }
  }, [debouncedSurname]);

  useEffect(() => {
    if (!first) {
      setChatSearchData({ email: debouncedEmail });
    }
  }, [debouncedEmail]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(getAllPermissionsByAccessType({ accessType: 'user' }));
  }, [dispatch]);

  const handleModalClose = () => {
    setIsChannelModalOpen(false);
    dispatch(getAllPermissionsByAccessType({ accessType: 'user' }));
  };

  return (
    <div>
      <SearchButton>
        <div>
          <Button
            active={active}
            onClick={() => setActive(!active)}
            className="action-button"
            variant={'search'}
          >
            Search
          </Button>
        </div>
      </SearchButton>

      {active && (
        <Filter
          name={name}
          setName={(value) => handleChange(value, 'name')}
          surname={surname}
          setSurname={(value) => handleChange(value, 'surname')}
          email={email}
          setEmail={(value) => handleChange(value, 'email')}
          handleClearAll={handleClearAll}
        />
      )}

      <TableWrapper>
        {!hasSearched ? (
          <EmptyState>
            <EmptyStateTitle>Search for Users</EmptyStateTitle>
            <EmptyStateText>
              Use the search filters above to find users and configure their channel
              permissions.
            </EmptyStateText>
          </EmptyState>
        ) : isMobile ? (
          <MobileList
            columns={mobileColumns}
            data={tableData}
            expandable={renderExpandable}
            loading={false}
            currentPage={currentPage}
            totalPages={pagesCount}
            onPaginationChange={onPaginationChange}
          />
        ) : (
          <Table
            onPaginationChange={onPaginationChange}
            totalPages={pagesCount}
            data={tableData}
            columns={columns}
            currentPage={currentPage}
            scrollable={true}
            pagination={true}
          />
        )}
      </TableWrapper>

      {selectedUser && (
        <ChannelConfigurator
          isOpen={isChannelModalOpen}
          onClose={handleModalClose}
          role={selectedUser}
          channelPermissions={channelPermissions}
          setChannelPermissions={setChannelPermissions}
          accessType="user"
        />
      )}
    </div>
  );
};

export default ByUser;
